import { useQuery } from '@apollo/client'
import { CacheControl } from '@tusbar/cache-control'
import { GraphQLFormattedError } from 'graphql'
import compact from 'lodash-es/compact'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { CollectionGridSectionFragmentFragment } from '../../../__generated__/graphql/catalog/graphql'
import { CollectionMeta } from '../../../brunswick/components/pages/Collection/CollectionMeta'
import { CollectionView } from '../../../brunswick/components/pages/Collection/CollectionView'
import { QuickBuyModal } from '../../../brunswick/components/QuickBuyModal'
import {
  COLLECTION_ITEM_MAX_ROW_SIZE,
  COLLECTION_ITEM_PAGE_SIZE,
  CONTENT_KEY,
  FILTERS_QUERY_PARAM,
  INCLUDE_UNAVAILABLE_PRODUCTS,
  LegacyFilterQueryParam,
  PREVIEW,
  PRODUCT_DATA_KEY,
} from '../../../brunswick/constants'
import { QuickBuyContainer } from '../../../brunswick/containers/QuickBuyContainer'
import { IterableContainer } from '../../../containers/iterable'
import { COLLECTION_PAGE_QUERY_OPTIMIZED } from '../../../lib/graphql/queries/COLLECTION_PAGE_QUERY_OPTIMIZED'
import { FETCH_COLLECTION_LAYOUT } from '../../../lib/graphql/queries/FETCH_COLLECTION_LAYOUT'
import {
  GET_COLLECTION,
  filterKeysFromFilterConfig,
  sanitizeFilterConfig,
} from '../../../lib/graphql/queries/GET_COLLECTION'
import { cachePolicies } from '../../../lib/response-cache'
import { isBrowser, isServer } from '../../../lib/utils'
import { singleQueryParamValue } from '../../../lib/utils'
import { deriveCollectionItemFilterInput } from '../../../lib/utils/collection-filters'
import { MagnoliaPage, MagnoliaPageContext, MagnoliaPageInitialProps } from '../MagnoliaApp'

import { redirectLegacyFilterQueryParams } from './redirectLegacyFilterQueryParams'
import { SchemaTags } from './SchemaTags'

type CollectionPageProps = MagnoliaPageInitialProps<{
  errors: Array<GraphQLFormattedError>
  handle: string
  includeUnavailable: boolean
  isPreview: boolean
  launchTags?: string
}>

export const CollectionPage: MagnoliaPage<CollectionPageProps> = ({
  errors,
  handle,
  includeUnavailable,
  isPreview,
  launchTags,
}) => {
  const { data, error, loading } = useQuery(FETCH_COLLECTION_LAYOUT, {
    ssr: isServer(),
    variables: { handle },
    context: { includeUnavailable, isPreview, launchTags },
  })
  const { pathname } = useRouter()
  const { trackCollectionView, isIterableReady } = IterableContainer.useContainer()

  useEffect(() => {
    trackCollectionView({ handle, path: pathname.replace('[handle]', handle) })
  }, [handle, pathname, isIterableReady, trackCollectionView])

  const collectionView = data?.collectionView

  // Default line item properties for garments added through quick buy
  const defaultGarmentProperties: Record<string, string> = { _collectionAttribution: handle }

  return (
    <QuickBuyContainer.Provider initialState={{ defaultGarmentProperties }}>
      <CollectionMeta collectionView={collectionView ?? undefined} />

      <CollectionView
        collectionView={collectionView ?? undefined}
        handle={handle}
        loading={loading}
        errors={compact([...errors, error])}
      />
      <QuickBuyModal />
      <SchemaTags collectionView={collectionView ?? undefined} />
    </QuickBuyContainer.Provider>
  )
}

CollectionPage.getInitialProps = function (pageContext: MagnoliaPageContext) {
  const handle: string = singleQueryParamValue(pageContext.query.handle, '')

  redirectLegacyFilterQueryParams({
    collectionFiltersQueryParam: pageContext.query[FILTERS_QUERY_PARAM],
    handle,
    isPlainPageQueryParam: pageContext.query['plain'],
    legacyCategoryQueryParam: pageContext.query[LegacyFilterQueryParam.Category],
    legacyColorQueryParam: pageContext.query[LegacyFilterQueryParam.Color],
    legacyFitQueryParam: pageContext.query[LegacyFilterQueryParam.Fit],
    pathname: pageContext.pathname,
    res: pageContext.res,
  })

  const isPreview: boolean = singleQueryParamValue(pageContext.query[CONTENT_KEY], '') === PREVIEW
  const productDataParams = singleQueryParamValue(pageContext.query[PRODUCT_DATA_KEY], '')
  const includeUnavailable: boolean = productDataParams === INCLUDE_UNAVAILABLE_PRODUCTS
  const launchTags: string = includeUnavailable ? '' : productDataParams

  return getInitialProps({ handle, includeUnavailable, isPreview, pageContext, launchTags })
}

async function getInitialProps({
  handle,
  includeUnavailable,
  isPreview,
  pageContext,
  launchTags,
}: {
  handle: string
  includeUnavailable: boolean
  isPreview: boolean
  pageContext: MagnoliaPageContext
  launchTags: string
}): Promise<CollectionPageProps> {
  const pageProps: CollectionPageProps = {
    errors: [],
    handle,
    includeUnavailable,
    isPreview,
    launchTags,
    responseCacheControl: cachePolicies.noCache,
  }

  /**
   * Render the page immediately by not blocking on getInitialProps and let the
   * page component run a fresh query client-side:
   */
  if (isBrowser()) return pageProps

  const client = pageContext.apolloClientFactory?.for(ApolloClientProfile.FigsPublicSupergraph)
  const cacheBypass =
    singleQueryParamValue(pageContext.query.features, '') === process.env.CACHE_BYPASS_FEATURE
  const explicitNoCache = isPreview || includeUnavailable || cacheBypass || !!launchTags.length

  const selectedFilters = deriveCollectionItemFilterInput({
    collectionFiltersQueryParam: pageContext.query[FILTERS_QUERY_PARAM],
    legacyCategoryQueryParam: pageContext.query[LegacyFilterQueryParam.Category],
    legacyColorQueryParam: pageContext.query[LegacyFilterQueryParam.Color],
    legacyFitQueryParam: pageContext.query[LegacyFilterQueryParam.Fit],
  })

  /**
   * Prime the Apollo Client cache for SSR:
   */
  const { data, errors } = await client.query({
    query: COLLECTION_PAGE_QUERY_OPTIMIZED,
    variables: { handle },
    errorPolicy: 'all',
    context: { includeUnavailable, isPreview, launchTags },
  })

  const editorializedCollections =
    data.collectionView?.sections.reduce<
      Array<{
        editorializedCollection: CollectionGridSectionFragmentFragment['editorializedCollections'][number]
        filterConfig: CollectionGridSectionFragmentFragment['filterConfig']
        itemColorScope: CollectionGridSectionFragmentFragment['itemColorScope']
        groupedProductTiles: CollectionGridSectionFragmentFragment['groupedProductTiles']
      }>
    >((accumulatedEditorializedCollections, nextSection) => {
      if (
        nextSection.__typename === 'CollectionGridSection' &&
        nextSection.editorializedCollections
      ) {
        const groupedProductTiles = nextSection.groupedProductTiles
        return accumulatedEditorializedCollections.concat(
          nextSection.editorializedCollections.map(editorializedCollection => {
            return {
              editorializedCollection,
              filterConfig: nextSection.filterConfig,
              itemColorScope: nextSection.itemColorScope,
              groupedProductTiles,
            }
          })
        )
      } else {
        return accumulatedEditorializedCollections
      }
    }, []) ?? []

  const collectionsData = await Promise.all(
    editorializedCollections.map((editorializedCollection, index) => {
      return client.query({
        query: GET_COLLECTION,
        variables: {
          collectionHandle: editorializedCollection.editorializedCollection.handle,
          collectionColorScope:
            editorializedCollection.itemColorScope === 'ALL' ? 'ALL' : 'COLLECTION',
          filterConfig: sanitizeFilterConfig(editorializedCollection.filterConfig),
          filterKeys: filterKeysFromFilterConfig(editorializedCollection.filterConfig),
          selectedFilters,
          nextPageDataOnly: false,
          first: index === 0 ? COLLECTION_ITEM_PAGE_SIZE : COLLECTION_ITEM_MAX_ROW_SIZE,
          grouping: editorializedCollection.groupedProductTiles ? 'PRODUCT_GROUP' : 'PRODUCT',
        },
        errorPolicy: 'all',
        context: { includeUnavailable, isPreview, launchTags },
      })
    })
  )

  const dataFetchingErrors: Array<GraphQLFormattedError> = errors?.slice() ?? []

  if (dataFetchingErrors.length > 0) {
    return {
      ...pageProps,
      errors: dataFetchingErrors,
      responseCacheControl: cachePolicies.noCache,
    }
  }

  let hasProducts: boolean = false
  try {
    hasProducts = !!collectionsData.find(
      o => o.data.collection?.items?.nodes && o.data.collection.items.nodes.length > 0
    )

    if (!hasProducts && pageContext.res) {
      pageContext.res.statusCode = 404
    }
  } catch (fetchCollectionsError) {
    if (pageContext.res) pageContext.res.statusCode = 404
    dataFetchingErrors.push(fetchCollectionsError as Error)
  }

  return {
    ...pageProps,
    errors: dataFetchingErrors,
    responseCacheControl: cachePolicy({
      errors: dataFetchingErrors,
      explicitNoCache,
      hasProducts,
    }),
  }
}

function cachePolicy({
  errors,
  explicitNoCache,
  hasProducts,
}: {
  errors: Array<GraphQLFormattedError> | undefined
  explicitNoCache: boolean
  hasProducts: boolean
}): CacheControl {
  return explicitNoCache || (errors && errors.length > 0) || !hasProducts
    ? cachePolicies.noCache
    : cachePolicies.publicHtmlPolicy
}
